/**
 * Documenti allegati in chat, quello che Otto ci legge, e le scadenze.
 *
 * Il percorso: l'host allega un PDF o una foto (il riparto del condominio, una
 * bolletta), Otto lo legge e propone i costi e le rate in una scheda, l'host
 * controlla i numeri accanto al documento e salva con un tap. I costi entrano
 * nei conti dell'appartamento (`costi_ricorrenti`); le rate diventano scadenze,
 * che dicono solo **quando** si paga e non entrano nel conto: lo stesso
 * condominio non si conta due volte.
 */

import { z } from "zod";
import { CATEGORIE_COSTO, PERIODICITA } from "./ledger.js";

/** I formati che il modello legge da solo: PDF e immagini. */
export const TIPI_ALLEGATO = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;
export type TipoAllegato = (typeof TIPI_ALLEGATO)[number];

/**
 * Dieci megabyte: un riparto di condominio scansionato ci sta largo. Le
 * immagini il modello le accetta fino a 5 MB l'una.
 */
export const BYTE_MAX_ALLEGATO = 10 * 1024 * 1024;
export const BYTE_MAX_IMMAGINE = 5 * 1024 * 1024;

/** Quanti allegati per messaggio. */
export const ALLEGATI_PER_MESSAGGIO = 3;

/**
 * Quanti documenti un account può caricare in 24 ore. Un host ne allega uno
 * ogni tanto (un riparto, una bolletta); senza un tetto, un account potrebbe
 * riempire il disco del server, dove stanno anche i backup (audit del
 * 7/10/2026).
 */
export const ALLEGATI_AL_GIORNO = 5;

/** Dopo quanti giorni un allegato mai usato in un messaggio si cancella. */
export const GIORNI_ALLEGATO_ORFANO = 7;

export interface AllegatoView {
  id: string;
  nome: string;
  tipo: TipoAllegato;
  byte: number;
}

const DataSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "data AAAA-MM-GG");

/**
 * Le categorie che un documento può portare. Niente commissioni dei canali e
 * niente imposte: quelle arrivano dal gestionale e dai conti, non da un PDF.
 */
export const CATEGORIE_DA_DOCUMENTO = CATEGORIE_COSTO.filter(
  (c) => c !== "commissioni_ota" && c !== "imposte",
) as unknown as readonly [
  Exclude<(typeof CATEGORIE_COSTO)[number], "commissioni_ota" | "imposte">,
  ...Exclude<(typeof CATEGORIE_COSTO)[number], "commissioni_ota" | "imposte">[],
];

export const CostoDaDocumentoSchema = z.object({
  categoria: z.enum(CATEGORIE_DA_DOCUMENTO),
  descrizione: z.string().trim().min(1).max(200),
  /** L'importo per periodo, come lo scrive il documento. */
  importoCents: z.number().int().min(1),
  periodicita: z.enum(PERIODICITA),
  dal: DataSchema,
  al: DataSchema.nullable(),
});
export type CostoDaDocumento = z.infer<typeof CostoDaDocumentoSchema>;

export const ScadenzaDaDocumentoSchema = z.object({
  descrizione: z.string().trim().min(1).max(200),
  importoCents: z.number().int().min(1),
  data: DataSchema,
  /** Come si paga: IBAN, intestatario, causale. Quello che il documento dice. */
  nota: z.string().trim().max(400).nullable(),
});
export type ScadenzaDaDocumento = z.infer<typeof ScadenzaDaDocumentoSchema>;

export const DatiPropostaDocumentoSchema = z.object({
  /** Il nome del documento, per dire da dove vengono i numeri. */
  fonte: z.string().trim().min(1).max(200),
  costi: z.array(CostoDaDocumentoSchema).max(20),
  scadenze: z.array(ScadenzaDaDocumentoSchema).max(24),
});
export type DatiPropostaDocumento = z.infer<typeof DatiPropostaDocumentoSchema>;

export type StatoPropostaDocumento = "proposta" | "salvata" | "scartata";

export interface ScadenzaView {
  id: string;
  listingId: string | null;
  listingNickname: string | null;
  descrizione: string;
  importoCents: number;
  data: string;
  nota: string | null;
  pagataIl: string | null;
}

export const NuovaScadenzaSchema = z.object({
  listingId: z.string().nullable(),
  descrizione: z.string().trim().min(1).max(200),
  importoCents: z.number().int().min(1),
  data: DataSchema,
  nota: z.string().trim().max(400).nullable().default(null),
});
export type NuovaScadenza = z.infer<typeof NuovaScadenzaSchema>;
